/* eslint-disable no-nested-ternary */
import React, { useContext, useState } from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import {
  IconButton,
  makeStyles,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import styled from 'styled-components';
import { flowRight as compose } from 'lodash';
import { EditContext, InputMode } from '.';
import { SELECTING, TYPING } from './constants';

const useStyles = makeStyles(() => ({
  input: {
    fontFamily: `"Muli", sans-serif`,
    fontSize: '14px',
    width: '100%',
  },
  item: {
    fontFamily: `"Muli", sans-serif`,
    fontSize: '14px',
  },
}));

const EditDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const InputDiv = styled.div`
  width: 70%;
`;

const extractEventValue = (
  e: React.ChangeEvent<{
    name?: string;
    value: unknown;
  }>,
) => e.target.value;

const onEventValue = (f1: (s: string) => any) => compose(f1, extractEventValue);

const ToggleEditMode = ({
  inputMode,
  setToTyping,
  resetToSelecting,
}: {
  inputMode: InputMode;
  setToTyping: () => void;
  resetToSelecting: () => void;
}) => (
  <div>
    {inputMode === SELECTING ? (
      <IconButton
        aria-label="toggle input mode (select or type)"
        size="small"
        onClick={setToTyping}
      >
        <EditIcon />
      </IconButton>
    ) : (
      <IconButton
        aria-label="toggle input mode (select or type)"
        size="small"
        onClick={resetToSelecting}
      >
        <CloseIcon />
      </IconButton>
    )}
  </div>
);

// @note Ag-Grid re-mounts the cell renderers every time redrawRows is called (which seems to occur when the EditContext fields change),
// @note (contd.) hence the need for this separate "set" and "dispatch" type logic when the user is typing
export const TypeRenderer = (params: ICellRendererParams | any) => {
  const {
    editState,
    setEditState,
    typeInputMode,
    setTypeInputMode,
  } = useContext(EditContext);

  const classes = useStyles();

  const typeOptions = params.types;

  const editing = editState && params.node.rowPinned === 'top';

  const [editedType, setEditedType] = useState(
    editing ? editState.incidenttype : '',
  );

  const dispatchType = (t: string) => {
    if (editState) {
      setEditState({ ...editState, incidenttype: t });
    }
  };

  const setAndDispatchType = (newType: string) => {
    setEditedType(newType);
    dispatchType(newType);
  };

  return (
    <div style={{ width: '100%' }}>
      {editing ? (
        <EditDiv>
          <InputDiv>
            {typeInputMode === SELECTING ? (
              <Select
                className={classes.input}
                value={editState.incidenttype}
                onChange={onEventValue(setAndDispatchType)}
              >
                {typeOptions.map((typeName: string) => (
                  <MenuItem className={classes.item} value={typeName as any}>
                    {typeName}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <TextField
                size="small"
                inputProps={{
                  style: {
                    fontSize: '14px',
                    backgroundColor: 'inherit',
                    height: '100%',
                  },
                }}
                value={editedType}
                onChange={onEventValue(setEditedType)}
                onPointerLeave={() => dispatchType(editedType)}
              />
            )}
          </InputDiv>
          <ToggleEditMode
            inputMode={typeInputMode}
            setToTyping={() => setTypeInputMode(TYPING)}
            resetToSelecting={() => {
              setTypeInputMode(SELECTING);
              setEditedType('');
            }}
          />
        </EditDiv>
      ) : (
        params.value
      )}
    </div>
  );
};
